import { createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "libs/api/utility_record";

function makeThunk(typePrefix, apiFn, argExtractor) {
  return createAsyncThunk(typePrefix, async (args) => {
    const { cbPending, cbFulfilled, cbRejected, cbFinally } = args || {};
    try {
      typeof cbPending === "function" && cbPending();
      const apiArgs = argExtractor(args);
      const res = await apiFn(...(Array.isArray(apiArgs) ? apiArgs : [apiArgs]));
      typeof cbFulfilled === "function" && cbFulfilled(res?.data);
      return res?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      }
      typeof cbRejected === "function" && cbRejected(error);
      throw JSON.stringify({ message: error.message });
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  });
}

// ── Electricity Rate Configs ──────────────────────────────────────────────────

export const getElecRateConfigsThunk = makeThunk(
  "utilityRecord/getElecRateConfigs",
  api.getElecRateConfigs,
  ({ params = "" } = {}) => params,
);

export const getElecRateConfigByIdThunk = makeThunk(
  "utilityRecord/getElecRateConfigById",
  api.getElecRateConfigById,
  ({ id }) => id,
);

export const createElecRateConfigThunk = makeThunk(
  "utilityRecord/createElecRateConfig",
  api.createElecRateConfig,
  ({ body }) => body,
);

export const updateElecRateConfigThunk = makeThunk(
  "utilityRecord/updateElecRateConfig",
  api.updateElecRateConfig,
  ({ id, body }) => [id, body],
);

// ── Electricity Records ───────────────────────────────────────────────────────

export const getElecRecordsThunk = makeThunk(
  "utilityRecord/getElecRecords",
  api.getElecRecords,
  ({ params = "" } = {}) => params,
);

export const getElecRecordByIdThunk = makeThunk(
  "utilityRecord/getElecRecordById",
  api.getElecRecordById,
  ({ id }) => id,
);

export const createElecRecordThunk = makeThunk(
  "utilityRecord/createElecRecord",
  api.createElecRecord,
  ({ body }) => body,
);

export const createElecRecordsBatchThunk = makeThunk(
  "utilityRecord/createElecRecordsBatch",
  api.createElecRecordsBatch,
  ({ body }) => body,
);

export const updateElecRecordThunk = makeThunk(
  "utilityRecord/updateElecRecord",
  api.updateElecRecord,
  ({ id, body }) => [id, body],
);

export const deleteElecRecordThunk = makeThunk(
  "utilityRecord/deleteElecRecord",
  api.deleteElecRecord,
  ({ id }) => id,
);

export const updateElecRecordStatusThunk = makeThunk(
  "utilityRecord/updateElecRecordStatus",
  api.updateElecRecordStatus,
  ({ id, body }) => [id, body],
);

export const getElecRecordSummaryTextThunk = makeThunk(
  "utilityRecord/getElecRecordSummaryText",
  api.getElecRecordSummaryText,
  ({ id }) => id,
);

export const calculateElecPreviewThunk = makeThunk(
  "utilityRecord/calculateElecPreview",
  api.calculateElecPreview,
  ({ body }) => body,
);

export const getElecUnitContextThunk = makeThunk(
  "utilityRecord/getElecUnitContext",
  api.getElecUnitContext,
  ({ unitId }) => unitId,
);

export const getElecPendingScheduleThunk = makeThunk(
  "utilityRecord/getElecPendingSchedule",
  api.getElecPendingSchedule,
  ({ buildingId }) => buildingId,
);

// ── Water Rate Configs ────────────────────────────────────────────────────────

export const getWaterRateConfigsThunk = makeThunk(
  "utilityRecord/getWaterRateConfigs",
  api.getWaterRateConfigs,
  ({ params = "" } = {}) => params,
);

export const getWaterRateConfigByIdThunk = makeThunk(
  "utilityRecord/getWaterRateConfigById",
  api.getWaterRateConfigById,
  ({ id }) => id,
);

export const createWaterRateConfigThunk = makeThunk(
  "utilityRecord/createWaterRateConfig",
  api.createWaterRateConfig,
  ({ body }) => body,
);

export const updateWaterRateConfigThunk = makeThunk(
  "utilityRecord/updateWaterRateConfig",
  api.updateWaterRateConfig,
  ({ id, body }) => [id, body],
);

// ── Water Records ─────────────────────────────────────────────────────────────

export const getWaterPendingScheduleThunk = makeThunk(
  "utilityRecord/getWaterPendingSchedule",
  api.getWaterPendingSchedule,
  ({ contractId }) => contractId,
);

export const getWaterUnitContextThunk = makeThunk(
  "utilityRecord/getWaterUnitContext",
  api.getWaterUnitContext,
  ({ unitId }) => unitId,
);

export const getWaterRecordsThunk = makeThunk(
  "utilityRecord/getWaterRecords",
  api.getWaterRecords,
  ({ params = "" } = {}) => params,
);

export const getWaterRecordByIdThunk = makeThunk(
  "utilityRecord/getWaterRecordById",
  api.getWaterRecordById,
  ({ id }) => id,
);

export const createWaterRecordThunk = makeThunk(
  "utilityRecord/createWaterRecord",
  api.createWaterRecord,
  ({ body }) => body,
);

export const createWaterRecordsBatchThunk = makeThunk(
  "utilityRecord/createWaterRecordsBatch",
  api.createWaterRecordsBatch,
  ({ body }) => body,
);

export const updateWaterRecordThunk = makeThunk(
  "utilityRecord/updateWaterRecord",
  api.updateWaterRecord,
  ({ id, body }) => [id, body],
);

export const deleteWaterRecordThunk = makeThunk(
  "utilityRecord/deleteWaterRecord",
  api.deleteWaterRecord,
  ({ id }) => id,
);

export const updateWaterRecordStatusThunk = makeThunk(
  "utilityRecord/updateWaterRecordStatus",
  api.updateWaterRecordStatus,
  ({ id, body }) => [id, body],
);

export const calculateWaterPreviewThunk = makeThunk(
  "utilityRecord/calculateWaterPreview",
  api.calculateWaterPreview,
  ({ body }) => body,
);
